import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtDate, fmtDateTime, fmtMoney, fmtNumber } from '../lib/format';
import { Button, Callout, Empty, ErrorState, Field, Input, Ltr, useConfirm, useToast } from '../components/ui';
import { CustomerPicker } from '../components/CustomerPicker';

export type ArchiveStatus = {
  connected: boolean;
  apiKeyId?: string;
  businessName?: string | null;
  businessTaxId?: string | null;
  connectedAt?: string;
  lastImport?: {
    startedAt: string;
    finishedAt: string | null;
    running: boolean;
    progress: string | null;
    error: string | null;
    stats: { found: number; added: number; linked: number; review: number; excluded: number; credited: number; skipped: number; already: number } | null;
  } | null;
  counts: { linked: number; review: number; ignored: number; lines: number; customers: number };
};
type ReviewItem = {
  clientKey: string;
  clientName: string;
  clientTaxId: string | null;
  invoices: number;
  lines: number;
  total: number;
  first: string;
  last: string;
  suggestions: Array<{ customerId: number; businessName: string; score: number }>;
};

export function useArchiveStatus(enabled = true) {
  return useQuery({
    queryKey: ['archive'],
    queryFn: () => api<ArchiveStatus>('/api/archive'),
    enabled,
    refetchInterval: (q) => (q.state.data?.lastImport?.running ? 2000 : false),
  });
}

/** Settings section: the MARKIT account as price history. */
export function ArchiveSettings() {
  const status = useArchiveStatus();
  const [apiKeyId, setApiKeyId] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    try {
      await fn();
      qc.invalidateQueries({ queryKey: ['archive'] });
      toast.success(ok);
      setSecret('');
      setApiKeyId('');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const s = status.data;
  if (!s) return null;
  const imp = s.lastImport;
  return (
    <div className="form-section">
      <div className="form-section-title">החשבון MARKIT — זיכרון מחירים</div>
      <p className="small muted" style={{ marginTop: 0 }}>
        חשבוניות מס מהחשבון הנוסף שסטטוס שלהן "סגור" או "נסגר ידנית" — בלי מבוטלות, בלי חשבוניות שיש להן זיכוי ובלי זיכויים. הן נכנסות רק לזיכרון המחירים של הלקוחות,
        לא ליתרות, למכירות או ללוח הבקרה. מוחרגים: עיריית רעננה והאגפים שלה, גן שמואל מזון, מזי אובר מעבדה.
      </p>
      {s.connected ? (
        <div className="stack">
          <dl className="kv">
            <dt>חשבון</dt>
            <dd>{s.businessName ?? '—'}{s.businessTaxId ? <> · ח.פ <Ltr>{s.businessTaxId}</Ltr></> : null} · מפתח <bdi className="ltr">{s.apiKeyId}</bdi></dd>
            <dt>בזיכרון</dt>
            <dd>
              {fmtNumber(s.counts.linked)} חשבוניות · {fmtNumber(s.counts.lines)} שורות · {fmtNumber(s.counts.customers)} לקוחות
              {s.counts.review ? <> · <Link to="/archive">{fmtNumber(s.counts.review)} חשבוניות של לקוחות לבדיקה</Link></> : null}
            </dd>
            <dt>ייבוא אחרון</dt>
            <dd>
              {imp?.running ? (
                <span><span className="spinner" /> {imp.progress ?? 'רץ…'}</span>
              ) : imp?.finishedAt ? (
                <>
                  {fmtDateTime(imp.finishedAt)}
                  {imp.stats ? ` · נמצאו ${fmtNumber(imp.stats.found)}, חדשות ${fmtNumber(imp.stats.added)} (${fmtNumber(imp.stats.linked)} ללקוחות קיימים, ${fmtNumber(imp.stats.review)} לבדיקה, ${fmtNumber(imp.stats.excluded)} מוחרגות) · ${fmtNumber(imp.stats.credited)} עם זיכוי דולגו` : ''}
                  {imp.error ? <span style={{ color: 'var(--bad)' }}> · {imp.error}</span> : null}
                </>
              ) : (
                'עדיין לא'
              )}
            </dd>
          </dl>
          <div className="row wrap">
            <Button variant="primary" loading={busy === 'import'} disabled={Boolean(imp?.running)} onClick={() => run('import', () => api('/api/archive/import', { body: {} }), 'הייבוא התחיל — אפשר להמשיך לעבוד')}>
              {s.counts.linked || s.counts.review ? 'ייבוא חשבוניות חדשות' : 'ייבוא החשבוניות'}
            </Button>
            {s.counts.review ? <Link to="/archive" className="btn">לקוחות לבדיקה ({fmtNumber(s.counts.review)})</Link> : null}
            <span className="spacer" />
            <Button variant="ghost" size="sm" loading={busy === 'off'} onClick={() => run('off', () => api('/api/archive/connect', { method: 'DELETE' }), 'החיבור הוסר')}>ניתוק</Button>
          </div>
        </div>
      ) : (
        <div className="stack">
          <p className="small" style={{ margin: 0 }}>
            במורנינג, מתוך החשבון <strong>MARKIT</strong> (מחליפים עסק בתפריט העליון): הגדרות ← כלים למפתחים ← API ← יצירת מפתח. המפתח משמש לקריאה בלבד.
          </p>
          <div className="form-grid">
            <Field label="Client ID (של MARKIT)">
              <Input value={apiKeyId} onChange={(e) => setApiKeyId(e.target.value)} ltr autoComplete="off" />
            </Field>
            <Field label="Client Secret (של MARKIT)">
              <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} ltr autoComplete="new-password" />
            </Field>
          </div>
          <div className="row">
            <Button onClick={() => run('connect', () => api('/api/archive/connect', { body: { apiKeyId: apiKeyId.trim(), secret: secret.trim() } }), 'החשבון MARKIT חובר')} loading={busy === 'connect'} disabled={apiKeyId.trim().length < 8 || secret.trim().length < 8}>
              חיבור ובדיקה
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Review: clients from the MARKIT account that did not match an existing customer. */
export function ArchiveReviewPage() {
  const { data, error, refetch } = useQuery({ queryKey: ['archive-review'], queryFn: () => api<{ items: ReviewItem[] }>('/api/archive/review') });
  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumbs"><Link to="/settings?tab=morning">הגדרות</Link></div>
          <h1>לקוחות מהחשבון MARKIT לבדיקה</h1>
          <div className="sub">
            לקוחות שהחשבוניות שלהם לא זוהו בוודאות. משייכים ללקוח קיים (המחירים נכנסים לזיכרון שלו), יוצרים לקוח חדש, או מתעלמים. השם נלמד — בפעם הבאה יזוהה אוטומטית.
          </div>
        </div>
      </div>
      {error ? (
        <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>
      ) : !data ? (
        <div className="panel panel-body"><span className="spinner" /></div>
      ) : data.items.length === 0 ? (
        <div className="panel"><Empty title="אין לקוחות לבדיקה" body="כל החשבוניות מהחשבון MARKIT שויכו או סומנו להתעלמות." /></div>
      ) : (
        <div className="stack">
          {data.items.map((it) => <ReviewCard key={it.clientKey} item={it} />)}
        </div>
      )}
    </>
  );
}

function ReviewCard({ item }: { item: ReviewItem }) {
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  useEffect(() => setCustomerId(item.suggestions[0]?.score && item.suggestions[0].score >= 0.6 ? item.suggestions[0].customerId : null), [item]);
  const done = (msg: string) => {
    toast.success(msg);
    for (const k of ['archive-review', 'archive', 'price-list', 'customers', 'customer-options']) qc.invalidateQueries({ queryKey: [k] });
  };
  const decide = async (key: string, body: Record<string, unknown>, msg: string) => {
    setBusy(key);
    try {
      await api('/api/archive/review', { body: { clientKey: item.clientKey, ...body } });
      done(msg);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const createAndAssign = async () => {
    if (!(await confirm({ title: `ליצור לקוח חדש "${item.clientName}"?`, body: 'הלקוח ייווצר עם השם והח.פ מהחשבוניות, והמחירים ייכנסו לזיכרון שלו. אפשר להשלים פרטים אחר כך.', confirmLabel: 'יצירה' }))) return;
    setBusy('create');
    try {
      const cn = item.clientTaxId && /^\d{5,9}$/.test(item.clientTaxId) ? item.clientTaxId : null;
      const r = await api<{ id: number }>('/api/customers', { body: { customer: { businessName: item.clientName, companyNumber: cn }, addresses: [], confirmDuplicate: true } });
      await api('/api/archive/review', { body: { clientKey: item.clientKey, action: 'assign', customerId: r.id } });
      done(`הלקוח "${item.clientName}" נוצר והמחירים נכנסו לזיכרון שלו`);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="panel">
      <div className="panel-head row wrap">
        <span className="strong">{item.clientName}</span>
        {item.clientTaxId ? <span className="small muted">ח.פ <Ltr>{item.clientTaxId}</Ltr></span> : <span className="small muted">ללא ח.פ</span>}
        <span className="spacer" />
        <span className="small muted">
          {fmtNumber(item.invoices)} חשבוניות · {fmtNumber(item.lines)} שורות · {fmtMoney(item.total)} · {fmtDate(item.first)}–{fmtDate(item.last)}
        </span>
      </div>
      <div className="panel-body stack" style={{ gap: 10 }}>
        {item.suggestions.length ? (
          <div className="row wrap" style={{ gap: 6 }}>
            <span className="small muted">דומה ל:</span>
            {item.suggestions.map((s) => (
              <Button key={s.customerId} size="sm" variant={customerId === s.customerId ? 'primary' : 'default'} onClick={() => setCustomerId(s.customerId)}>
                {s.businessName} <span className="faint">{Math.round(s.score * 100)}%</span>
              </Button>
            ))}
          </div>
        ) : (
          <Callout>לא נמצא לקוח דומה — כנראה לקוח חדש.</Callout>
        )}
        <div className="row wrap" style={{ gap: 8 }}>
          <div style={{ flex: '1 1 260px', maxWidth: 380 }}>
            <CustomerPicker value={customerId} onChange={(id) => setCustomerId(id)} />
          </div>
          <Button variant="primary" disabled={!customerId} loading={busy === 'assign'} onClick={() => decide('assign', { action: 'assign', customerId }, 'שויך — המחירים נכנסו לזיכרון של הלקוח')}>
            שיוך ללקוח
          </Button>
          <Button loading={busy === 'create'} onClick={createAndAssign}>לקוח חדש</Button>
          <Button variant="ghost" loading={busy === 'ignore'} onClick={() => decide('ignore', { action: 'ignore' }, 'סומן להתעלמות')}>התעלמות</Button>
        </div>
      </div>
    </section>
  );
}

type ArchiveInvoice = { id: number; number: string; issueDate: string; total: number; closedManually: boolean; open?: boolean; typeLabel?: string; branch: string | null; lines: Array<{ catalogNum: string | null; description: string; quantity: number; unitPrice: number }> };

/** The customer's invoices from the MARKIT account — read only, never counted in balances or reports. */
export function CustomerArchiveInvoices({ customerId }: { customerId: number }) {
  const { data } = useQuery({ queryKey: ['customer-archive', customerId], queryFn: () => api<{ items: ArchiveInvoice[] }>(`/api/customers/${customerId}/archive`) });
  const [open, setOpen] = useState<number | null>(null);
  if (!data?.items.length) return null;
  return (
    <section className="panel" style={{ marginTop: 16 }}>
      <div className="panel-head row wrap">
        <h2>חשבוניות מהחשבון MARKIT</h2>
        <span className="badge outline">היסטוריה בלבד</span>
        <span className="spacer" />
        <span className="small muted">{fmtNumber(data.items.length)} חשבוניות · לא נכללות ביתרות, בדוחות ובמכירות — רק בזיכרון המחירים</span>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>מספר</th>
              <th>תאריך</th>
              <th className="num">סה״כ כולל מע״מ</th>
              <th className="num">שורות</th>
              <th>סטטוס במורנינג</th>
              <th style={{ width: 1 }} />
            </tr>
          </thead>
          <tbody>
            {data.items.map((inv) => (
              <Fragment key={inv.id}>
                <tr className="clickable" onClick={() => setOpen(open === inv.id ? null : inv.id)}>
                  <td className="tabular strong"><Ltr>{inv.number}</Ltr>{inv.branch ? <span className="sub">{inv.branch}</span> : null}</td>
                  <td className="tabular">{fmtDate(inv.issueDate)}</td>
                  <td className="num tabular">{fmtMoney(inv.total)}</td>
                  <td className="num tabular">{fmtNumber(inv.lines.length)}</td>
                  <td className="small">
                    {inv.typeLabel && inv.typeLabel !== 'חשבונית מס' ? <span className="sub" style={{ display: 'block' }}>{inv.typeLabel}</span> : null}
                    {inv.open ? <span className="badge warn" title="לא סומנה כשולמה בחשבון MARKIT">פתוחה</span> : inv.closedManually ? 'נסגרה ידנית' : 'שולמה'}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <a className="btn sm" href={`/api/customers/${customerId}/archive/${inv.id}/pdf`} target="_blank" rel="noreferrer noopener">PDF</a>
                  </td>
                </tr>
                {open === inv.id ? (
                  <tr>
                    <td colSpan={6} style={{ background: 'var(--surface-2)' }}>
                      <table className="table">
                        <tbody>
                          {inv.lines.map((l, i) => (
                            <tr key={i}>
                              <td className="small muted"><Ltr>{l.catalogNum ?? ''}</Ltr></td>
                              <td>{l.description}</td>
                              <td className="num tabular">{fmtNumber(l.quantity)}</td>
                              <td className="num tabular">{fmtMoney(l.unitPrice)} <span className="faint small">ליח׳ לפני מע״מ</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
